import { Component, defineElement } from '@theme/component';

class YiseCustomizedBundle extends Component {
  ready() {
    if (this.initialized || !this.isConnected) return;
    const navigation = globalThis.performance?.getEntriesByType?.('navigation')?.[0];
    const beforeDocumentReady = document.readyState === 'loading' || (document.readyState === 'interactive' && !navigation?.domContentLoadedEventStart);
    if (!this.documentReady && beforeDocumentReady) {
      if (this.waitingForDocument) return;
      this.waitingForDocument = true;
      const initialize = () => {
        this.documentReady = true;
        this.waitingForDocument = false;
        this.ready();
      };
      this.listen(document, 'DOMContentLoaded', initialize, { once: true });
      this.listen(window, 'load', initialize, { once: true });
      return;
    }
    const bundleContainer = this.querySelector('[data-customized-bundle-root]');
    const addToCartBtn = bundleContainer?.querySelector('#custom-bundle-add-to-cart');
    if (!bundleContainer || !addToCartBtn?.querySelector('.custom-bundle_original-price')) {
      this.waitForMarkup();
      return;
    }
    const bundleType = bundleContainer.getAttribute('data-bundle-type') || 'Build Your Own Set';
    const isVbdl = bundleType === 'Virtual Bundle SKUs';
    let vbdlParents = [];
    if (isVbdl) {
      const dataEl = bundleContainer.querySelector('script[data-vbdl-parent-variants]');
      try { vbdlParents = JSON.parse(dataEl?.textContent || ''); } catch {
        this.waitForMarkup();
        return;
      }
      if (!Array.isArray(vbdlParents)) {
        this.waitForMarkup();
        return;
      }
    }
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.initialized = true;
    const owner = this;
    const mountToken = {};
    this.mountToken = mountToken;
    const isCurrent = () => owner.isConnected && owner.mountToken === mountToken;

    const parentVariantId = bundleContainer.getAttribute('data-parent-variant-id');
    const statusEl = bundleContainer.querySelector('[data-custom-bundle-status]');
    const errorEl = bundleContainer.querySelector('[data-custom-bundle-error]');

    function findMatchedParent() {
      if (!isVbdl || !vbdlParents.length) return null;
      const selectedIds = Object.values(selectedVariants)
        .map(v => v && v.variantId ? Number(v.variantId) : null)
        .filter(Boolean)
        .sort((a, b) => a - b);
      if (!selectedIds.length) return null;
      return vbdlParents.find(pv => {
        if (!Array.isArray(pv.components) || pv.components.length !== selectedIds.length) return false;
        const sorted = pv.components.slice().sort((a, b) => a - b);
        return sorted.every((id, i) => id === selectedIds[i]);
      }) || null;
    }

    let pendingParentVariantId = null;
    let productReadyBound = false;

    function syncProductVariant(variantId) {
      if (!variantId) return;
      pendingParentVariantId = variantId;
      const productForm = bundleContainer.closest('yise-product-form[data-template-product]');
      if (!productForm) return;
      const sync = () => {
        if (!isCurrent()) return;
        const selectedId = pendingParentVariantId;
        const needsInitialSync = productForm.dataset.bundleInitialVariantId === String(selectedId);
        if (String(productForm.currentVariant?.id) === String(selectedId) && !needsInitialSync) return;
        if (needsInitialSync && productForm.initialized) productForm.restoreBundleInitialHistory?.();
        if (productForm.selectVariantById?.(selectedId, { announce: false })) {
          if (needsInitialSync) delete productForm.dataset.bundleInitialVariantId;
          return;
        }
        if (productReadyBound) return;
        productReadyBound = true;
        owner.listen(productForm, 'yise:product-form-ready', () => {
          productReadyBound = false;
          sync();
        }, { once: true });
      };
      sync();
    }

    function maybeSyncVbdlVariant() {
      if (!isVbdl) return;
      const matched = findMatchedParent();
      if (!matched) return;
      syncProductVariant(matched.id);
    }

    function announceStatus(message) {
      if (!statusEl || !isCurrent()) return;
      statusEl.textContent = '';
      cancelAnimationFrame(owner.announcementFrame);
      owner.announcementFrame = requestAnimationFrame(function() {
        if (isCurrent()) statusEl.textContent = message;
      });
    }

    function clearError() {
      if (!errorEl || !isCurrent()) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function showError(message) {
      if (!errorEl || !isCurrent()) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function announceVariantSelection(categoryIndex, label) {
      const category = bundleContainer.querySelector(`.custom-bundle_category[data-category-index="${categoryIndex}"]`);
      const categoryTitle = category && category.getAttribute('data-category-title') || 'Bundle';
      if (isVbdl) {
        const matched = findMatchedParent();
        const availability = !matched
          ? ' This combination is not available.'
          : (matched.available === false ? ' This combination is out of stock.' : ' Available.');
        announceStatus(`${categoryTitle}: ${label} selected.${availability}`);
        return;
      }
      const selectedVariant = selectedVariants[categoryIndex];
      const availability = selectedVariant && selectedVariant.available === false ? ' This option is out of stock.' : ' Available.';
      announceStatus(`${categoryTitle}: ${label} selected.${availability}`);
    }

    function restoreButton(btn, originalText) {
      if (!isCurrent()) return;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('aria-busy');
      delete btn.dataset.loading;
      btn.innerHTML = originalText;
      if (owner.pendingButtonState?.button === btn) owner.pendingButtonState = null;
    }

    function setBundleCartAttributes(attributes) {
      if (window.YISECart && typeof window.YISECart.setCartAttributes === 'function') {
        return window.YISECart.setCartAttributes(attributes);
      }
      return Promise.reject(new Error('Cart attributes are unavailable'));
    }

    // Track selected variants per category
    const selectedVariants = {};

    // Initialize selected variants from first options
    function initializeSelections() {
      const categories = bundleContainer.querySelectorAll('.custom-bundle_category');
      
      categories.forEach((category) => {
        const categoryIndex = parseInt(category.getAttribute('data-category-index'));
        const swatchInput = category.querySelector('.custom-bundle_swatch-input:checked');
        const selectEl = category.querySelector('.custom-bundle_variant-select');
        const singleVariant = category.querySelector('.custom-bundle_single-variant');
        
        if (swatchInput) {
          const variantPrice = parseInt(swatchInput.getAttribute('data-variant-price')) || 0;
          const bundlePrice = parseInt(swatchInput.getAttribute('data-variant-bundle-price')) || variantPrice;
          selectedVariants[categoryIndex] = {
            variantId: swatchInput.getAttribute('data-variant-id'),
            price: variantPrice,
            compareAtPrice: swatchInput.getAttribute('data-variant-compare-price') ? parseInt(swatchInput.getAttribute('data-variant-compare-price')) : null,
            bundlePrice: bundlePrice,
            title: swatchInput.getAttribute('data-variant-title'),
            productTitle: swatchInput.getAttribute('data-product-title'),
            image: swatchInput.getAttribute('data-variant-image'),
            displayLabel: swatchInput.getAttribute('data-display-label'),
            available: swatchInput.getAttribute('data-variant-available') === 'true'
          };
          
          // Update selected label on init
          updateSelectedLabel(categoryIndex, swatchInput.getAttribute('data-display-label'));
        } else if (selectEl) {
          const selectedOption = selectEl.options[selectEl.selectedIndex];
          const variantPrice = parseInt(selectedOption.getAttribute('data-variant-price')) || 0;
          const bundlePrice = parseInt(selectedOption.getAttribute('data-variant-bundle-price')) || variantPrice;
          selectedVariants[categoryIndex] = {
            variantId: selectedOption.getAttribute('data-variant-id'),
            price: variantPrice,
            compareAtPrice: selectedOption.getAttribute('data-variant-compare-price') ? parseInt(selectedOption.getAttribute('data-variant-compare-price')) : null,
            bundlePrice: bundlePrice,
            title: selectedOption.getAttribute('data-variant-title'),
            productTitle: selectedOption.getAttribute('data-product-title'),
            image: selectedOption.getAttribute('data-variant-image'),
            displayLabel: selectedOption.getAttribute('data-display-label'),
            available: selectedOption.getAttribute('data-variant-available') === 'true'
          };
        } else if (singleVariant) {
          // Single variant category
          const variantPrice = parseInt(singleVariant.getAttribute('data-variant-price')) || 0;
          const bundlePrice = parseInt(singleVariant.getAttribute('data-variant-bundle-price')) || variantPrice;
          selectedVariants[categoryIndex] = {
            variantId: singleVariant.getAttribute('data-variant-id'),
            price: variantPrice,
            compareAtPrice: singleVariant.getAttribute('data-variant-compare-price') ? parseInt(singleVariant.getAttribute('data-variant-compare-price')) : null,
            bundlePrice: bundlePrice,
            title: singleVariant.getAttribute('data-variant-title'),
            productTitle: singleVariant.getAttribute('data-product-title'),
            image: singleVariant.getAttribute('data-variant-image'),
            available: singleVariant.getAttribute('data-variant-available') === 'true'
          };
        }
      });
      
      updatePricing();
      updateButtonState();
      maybeSyncVbdlVariant();
    }

    // Update pricing display
    function updatePricing() {
      const discountedPriceEl = bundleContainer.querySelector('.custom-bundle_discounted-price');
      const originalPriceEl = bundleContainer.querySelector('.custom-bundle_original-price');

      if (isVbdl) {
        const matched = findMatchedParent();
        if (matched) {
          const formatted = '$' + (matched.price / 100).toFixed(2).replace('.00', '');
          if (discountedPriceEl) discountedPriceEl.textContent = '- ' + formatted;
          if (originalPriceEl) {
            if (matched.compare_at_price && matched.compare_at_price > matched.price) {
              const formattedCompare = '$' + (matched.compare_at_price / 100).toFixed(2).replace('.00', '');
              originalPriceEl.textContent = formattedCompare;
              originalPriceEl.style.display = 'inline';
            } else {
              originalPriceEl.style.display = 'none';
            }
          }
        } else {
          if (discountedPriceEl) discountedPriceEl.textContent = '';
          if (originalPriceEl) originalPriceEl.style.display = 'none';
        }
        return;
      }

      let totalBundlePrice = 0;
      let totalCompareAtPrice = 0;
      let hasCompareAt = false;

      Object.values(selectedVariants).forEach(variant => {
        if (variant) {
          // Sum up bundle prices for the final price
          totalBundlePrice += variant.bundlePrice;
          // Use compare_at_price for strikethrough if available
          if (variant.compareAtPrice) {
            totalCompareAtPrice += variant.compareAtPrice;
            hasCompareAt = true;
          } else {
            totalCompareAtPrice += variant.price;
          }
        }
      });

      // Format prices (convert from cents to dollars)
      const formattedBundlePrice = '$' + (totalBundlePrice / 100).toFixed(2).replace('.00', '');
      const formattedCompareAtPrice = '$' + (totalCompareAtPrice / 100).toFixed(2).replace('.00', '');

      if (discountedPriceEl) {
        discountedPriceEl.textContent = '- ' + formattedBundlePrice;
      }

      // Show strikethrough if bundle price is less than compare at price
      if (originalPriceEl) {
        if (totalBundlePrice < totalCompareAtPrice) {
          originalPriceEl.textContent = formattedCompareAtPrice;
          originalPriceEl.style.display = 'inline';
        } else {
          originalPriceEl.style.display = 'none';
        }
      }
    }

    // Update category image
    function updateCategoryImage(categoryIndex, imageUrl) {
      const imageEl = bundleContainer.querySelector(`[data-category-image="${categoryIndex}"]`);
      if (imageEl && imageUrl) {
        imageEl.src = imageUrl;
      }
    }

    // Update selected label for swatches
    function updateSelectedLabel(categoryIndex, label) {
      const labelEl = bundleContainer.querySelector(`.custom-bundle_selected-label[data-category-index="${categoryIndex}"]`);
      if (labelEl && label) {
        labelEl.textContent = label;
      }
    }

    // Update variant caption (with optional size)
    function updateVariantCaption(categoryIndex, caption, defaultSize) {
      const captionEl = bundleContainer.querySelector(`.custom-bundle_variant-caption[data-category-index="${categoryIndex}"]`);
      if (captionEl) {
        let fullCaption = '';
        const cap = caption ? caption.trim() : '';
        const size = defaultSize ? defaultSize.trim() : '';
        if (cap && size) {
          fullCaption = cap + ' \u00B7 ' + size;
        } else if (cap) {
          fullCaption = cap;
        } else if (size) {
          fullCaption = size;
        }
        if (fullCaption) {
          captionEl.textContent = fullCaption;
          captionEl.style.display = '';
        } else {
          captionEl.textContent = '';
          captionEl.style.display = 'none';
        }
      }
    }

    // Check availability and update button state
    function updateButtonState() {
      const buttonTextEl = addToCartBtn.querySelector('.custom-bundle_button-text');
      const buttonPriceEl = addToCartBtn.querySelector('.custom-bundle_button-price');

      if (isVbdl) {
        const matched = findMatchedParent();
        if (!matched) {
          addToCartBtn.setAttribute('aria-disabled', 'true');
          addToCartBtn.classList.add('is-sold-out');
          if (buttonTextEl) buttonTextEl.textContent = 'Combination Unavailable';
          if (buttonPriceEl) buttonPriceEl.style.display = 'none';
        } else if (!matched.available) {
          addToCartBtn.setAttribute('aria-disabled', 'true');
          addToCartBtn.classList.add('is-sold-out');
          if (buttonTextEl) buttonTextEl.textContent = 'Out of Stock';
          if (buttonPriceEl) buttonPriceEl.style.display = 'none';
        } else {
          addToCartBtn.removeAttribute('aria-disabled');
          addToCartBtn.classList.remove('is-sold-out');
          if (buttonTextEl) buttonTextEl.textContent = 'Add to Bag\u00A0';
          if (buttonPriceEl) buttonPriceEl.style.display = '';
        }
        return;
      }

      const allAvailable = Object.values(selectedVariants).every(variant => variant && variant.available !== false);

      if (allAvailable) {
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.classList.remove('is-sold-out');
        if (buttonTextEl) buttonTextEl.textContent = 'Add to Bag\u00A0';
        if (buttonPriceEl) buttonPriceEl.style.display = '';
      } else {
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('is-sold-out');
        if (buttonTextEl) buttonTextEl.textContent = 'Out of Stock';
        if (buttonPriceEl) buttonPriceEl.style.display = 'none';
      }
    }

    // Handle swatch selection
    const swatchInputs = bundleContainer.querySelectorAll('.custom-bundle_swatch-input');
    swatchInputs.forEach(input => {
      owner.listen(input, 'change', function() {
        const category = this.closest('.custom-bundle_category');
        const categoryIndex = parseInt(category.getAttribute('data-category-index'));
        const displayLabel = this.getAttribute('data-display-label');
        const variantPrice = parseInt(this.getAttribute('data-variant-price')) || 0;
        const bundlePrice = parseInt(this.getAttribute('data-variant-bundle-price')) || variantPrice;
        
        selectedVariants[categoryIndex] = {
          variantId: this.getAttribute('data-variant-id'),
          price: variantPrice,
          compareAtPrice: this.getAttribute('data-variant-compare-price') ? parseInt(this.getAttribute('data-variant-compare-price')) : null,
          bundlePrice: bundlePrice,
          title: this.getAttribute('data-variant-title'),
          productTitle: this.getAttribute('data-product-title'),
          image: this.getAttribute('data-variant-image'),
          displayLabel: displayLabel,
          available: this.getAttribute('data-variant-available') === 'true'
        };
        
        // Update image
        updateCategoryImage(categoryIndex, this.getAttribute('data-variant-image'));
        
        // Update selected label
        updateSelectedLabel(categoryIndex, displayLabel);
        
        // Update variant caption
        updateVariantCaption(categoryIndex, this.getAttribute('data-variant-caption'), this.getAttribute('data-default-size'));

        updatePricing();
        updateButtonState();
        maybeSyncVbdlVariant();
        clearError();
        announceVariantSelection(categoryIndex, displayLabel);
      });
    });

    // Handle dropdown selection
    const selectEls = bundleContainer.querySelectorAll('.custom-bundle_variant-select');
    selectEls.forEach(select => {
      owner.listen(select, 'change', function() {
        const categoryIndex = parseInt(this.getAttribute('data-category-index'));
        const selectedOption = this.options[this.selectedIndex];
        const variantPrice = parseInt(selectedOption.getAttribute('data-variant-price')) || 0;
        const bundlePrice = parseInt(selectedOption.getAttribute('data-variant-bundle-price')) || variantPrice;
        
        selectedVariants[categoryIndex] = {
          variantId: selectedOption.getAttribute('data-variant-id'),
          price: variantPrice,
          compareAtPrice: selectedOption.getAttribute('data-variant-compare-price') ? parseInt(selectedOption.getAttribute('data-variant-compare-price')) : null,
          bundlePrice: bundlePrice,
          title: selectedOption.getAttribute('data-variant-title'),
          productTitle: selectedOption.getAttribute('data-product-title'),
          image: selectedOption.getAttribute('data-variant-image'),
          displayLabel: selectedOption.getAttribute('data-display-label'),
          available: selectedOption.getAttribute('data-variant-available') === 'true'
        };
        
        // Update image
        updateCategoryImage(categoryIndex, selectedOption.getAttribute('data-variant-image'));
        
        // Update variant caption
        updateVariantCaption(categoryIndex, selectedOption.getAttribute('data-variant-caption'), selectedOption.getAttribute('data-default-size'));

        updatePricing();
        updateButtonState();
        maybeSyncVbdlVariant();
        clearError();
        announceVariantSelection(categoryIndex, selectedOption.getAttribute('data-display-label'));
      });
    });

    // Add to cart functionality
    if (addToCartBtn) {
      owner.listen(addToCartBtn, 'click', function() {
        const btn = this;
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const originalText = btn.innerHTML;
        owner.pendingButtonState = { button: btn, markup: originalText, ariaDisabled: btn.getAttribute('aria-disabled'), loading: btn.dataset.loading };
        const parentNumericVariantId = btn.getAttribute('data-parent-variant-id');
        
        btn.setAttribute('aria-disabled', 'true');
        btn.dataset.loading = 'true';
        /* The busy ring stands in for the label. Rewriting the button's markup here
           would take the price spans with it, and `#custom-bundle-add-to-cart` is
           re-read by its own compare-price child on every remount. */
        btn.setAttribute('aria-busy', 'true');

        // Check if miniCart is available
        if (!window.miniCart || typeof window.miniCart.addToCart !== 'function') {
          console.error('miniCart functionality not available');
          showError('Unable to add items to cart. Please refresh the page and try again.');
          restoreButton(btn, originalText);
          return;
        }

        // Virtual Bundle SKUs: add single matched parent variant as a regular line item
        if (isVbdl) {
          const matched = findMatchedParent();
          if (!matched) {
            showError('This combination is not available. Choose a different set of options.');
            restoreButton(btn, originalText);
            return;
          } 

          // Add pending item for optimistic UI (matches non-VBDL flow)
          let pendingVbdlKey = null;
          if (typeof window.miniCart.addPendingItem === 'function') {
            const cartFormDataScript = bundleContainer.querySelector('script.cart-form-product-data');
            let parentBundleData = null;
            if (cartFormDataScript) {
              try {
                parentBundleData = JSON.parse(cartFormDataScript.textContent);
              } catch (e) {
                console.error('Error parsing cart-form-product-data:', e);
              }
            }

            const pendingBundleProducts = Object.values(selectedVariants).map(variant => {
              return {
                product_title: variant.productTitle || 'Product',
                variant_title: variant.displayLabel || variant.title || '',
                image: variant.image || ''
              };
            });

            const pendingItemData = {
              id: matched.id,
              quantity: 1,
              product_title: parentBundleData ? parentBundleData.product_title : (matched.title || 'Bundle'),
              variant_title: matched.title || (parentBundleData ? parentBundleData.variant_title : ''),
              price: matched.price,
              url: parentBundleData ? parentBundleData.url : '',
              featured_image: {
                url: parentBundleData ? parentBundleData.image : '',
                alt: parentBundleData ? parentBundleData.product_title : (matched.title || 'Bundle')
              },
              bundle_products: pendingBundleProducts
            };

            pendingVbdlKey = window.miniCart.addPendingItem(pendingItemData);
          }

          if (window.YISECart && typeof window.YISECart.openCart === 'function') {
            window.YISECart.openCart({ forAdd: true });
          }

          window.miniCart.addToCart([{ id: matched.id, quantity: 1 }])
            .then(() => {
              clearError();
              restoreButton(btn, originalText);
              announceStatus('Bundle added to bag.');
            })
            .catch(error => {
              console.error('Error adding bundle SKU to cart:', error);
              if (pendingVbdlKey && typeof window.miniCart.removePendingItem === 'function') {
                window.miniCart.removePendingItem(pendingVbdlKey);
              }
              showError('Unable to add the bundle to bag. Please try again.');
              restoreButton(btn, originalText);
            });
          return;
        }

        // Add pending item for optimistic UI
        let pendingBundleKey = null;
        if (window.miniCart && typeof window.miniCart.addPendingItem === 'function' && parentNumericVariantId) {
          // Get parent bundle data from the cart-form-product-data snippet
          const cartFormDataScript = bundleContainer.querySelector('script.cart-form-product-data');
          let parentBundleData = null;
          
          if (cartFormDataScript) {
            try {
              parentBundleData = JSON.parse(cartFormDataScript.textContent);
            } catch (e) {
              console.error('Error parsing cart-form-product-data:', e);
            }
          }
          
          // Calculate total price using bundle prices
          let pendingBundleTotal = 0;
          Object.values(selectedVariants).forEach(variant => {
            if (variant && variant.bundlePrice) {
              pendingBundleTotal += variant.bundlePrice;
            }
          });
          
          // Build bundle_products array for the pending item
          const pendingBundleProducts = Object.values(selectedVariants).map(variant => {
            return {
              product_title: variant.productTitle || 'Product',
              variant_title: variant.displayLabel || variant.title || '',
              image: variant.image || ''
            };
          });
          
          const pendingItemData = {
            id: parseInt(parentNumericVariantId),
            quantity: 1,
            product_title: parentBundleData ? parentBundleData.product_title : 'Custom Bundle',
            variant_title: parentBundleData ? parentBundleData.variant_title : '',
            price: Math.round(pendingBundleTotal),
            url: parentBundleData ? parentBundleData.url : '',
            featured_image: {
              url: parentBundleData ? parentBundleData.image : '',
              alt: parentBundleData ? parentBundleData.product_title : 'Custom Bundle'
            },
            bundle_products: pendingBundleProducts
          };
          
          pendingBundleKey = window.miniCart.addPendingItem(pendingItemData);
          
          // Open the drawer on the shared add beat, with the bundle parent standing in for the set
          if (window.YISECart && typeof window.YISECart.openCart === 'function') {
            window.YISECart.openCart({ forAdd: true });
          }
        }
        
        // Generate unique bundle ID for this bundle instance
        const bundleInstanceId = Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
        
        // Build cart items - calculate total bundle price first
        let totalBundlePrice = 0;
        Object.values(selectedVariants).forEach(variant => {
          if (variant && variant.bundlePrice) {
            totalBundlePrice += variant.bundlePrice;
          }
        });
        
        const cartItems = [];
        
        Object.values(selectedVariants).forEach((variant, index) => {
          if (variant && variant.variantId) {
            cartItems.push({
              quantity: 1,
              id: variant.variantId,
              properties: {
                '_byo-set': 'true',
                '_byo-final-price': totalBundlePrice,
                '_parent-bundle-variant-id': parentVariantId,
                '_custom-bundle': 'true',
                '_bundle-instance-id': bundleInstanceId
              }
            });
          }
        });
        
        // Reverse order so items appear in opposite order in cart
        cartItems.reverse();
        
        if (cartItems.length === 0) {
          showError('Select a product for each bundle option before adding to bag.');
          // Remove pending item on error
          if (pendingBundleKey && window.miniCart && typeof window.miniCart.removePendingItem === 'function') {
            window.miniCart.removePendingItem(pendingBundleKey);
          }
          restoreButton(btn, originalText);
          return;
        }
        
        // Add bundle items as new line items
        window.miniCart.addToCart(cartItems)
          .then(() => {
            return setBundleCartAttributes({ '_has_custom_bundle': 'true' });
          })
          .then(() => {
            clearError();
            restoreButton(btn, originalText);
            announceStatus('Bundle added to bag.');
          })
          .catch(error => {
            console.error('Error adding bundle to cart:', error);
            // Remove pending item on error
            if (pendingBundleKey && window.miniCart && typeof window.miniCart.removePendingItem === 'function') {
              window.miniCart.removePendingItem(pendingBundleKey);
            }
            showError('Unable to add the bundle to bag. Please try again.');
            restoreButton(btn, originalText);
          });
      });
    }

    // Handle "View details" clicks - scroll to bundle details and slide to product
    bundleContainer.querySelectorAll('[data-bpd-scroll]').forEach(function(link) {
      owner.listen(link, 'click', function(e) {
        e.preventDefault();
        var slideIndex = parseInt(this.getAttribute('data-bpd-scroll'));
        var bpdWrap = bundleContainer.closest('yise-product-form[data-template-product]')?.querySelector('.bpd_wrap');
        if (!bpdWrap) return;

        // Slide swiper to the correct product
        var swiperEl = bpdWrap.querySelector('.bpd_swiper');
        if (swiperEl && swiperEl.swiper) {
          swiperEl.swiper.slideTo(slideIndex);
        }

        // Scroll to top of component with padding for sticky header
        var rect = bpdWrap.getBoundingClientRect();
        var scrollTop = window.pageYOffset + rect.top - 20;
        var header = document.querySelector('header');
        if (header) {
          scrollTop -= header.offsetHeight;
        }
        window.scrollTo({ top: scrollTop, behavior: 'smooth' });
      });
    });

    // Initialize
    initializeSelections();
  }

  waitForMarkup() {
    const signal = this.abortController?.signal;
    if (this.markupObserver || !signal || signal.aborted || typeof MutationObserver === 'undefined') return;
    this.markupObserver = new MutationObserver(() => {
      if (!signal.aborted && this.abortController?.signal === signal) this.ready();
    });
    this.markupObserver.observe(this, { childList: true, subtree: true, characterData: true });
  }

  teardown() {
    this.waitingForDocument = false;
    this.markupObserver?.disconnect();
    this.markupObserver = null;
    this.initialized = false;
    const pending = this.pendingButtonState;
    if (pending) {
      pending.button.innerHTML = pending.markup;
      pending.button.removeAttribute('aria-busy');
      if (pending.ariaDisabled === null) pending.button.removeAttribute('aria-disabled');
      else pending.button.setAttribute('aria-disabled', pending.ariaDisabled);
      if (pending.loading === undefined) delete pending.button.dataset.loading;
      else pending.button.dataset.loading = pending.loading;
      this.pendingButtonState = null;
    }
    this.mountToken = null;
    cancelAnimationFrame(this.announcementFrame);
  }
}

defineElement('yise-customized-bundle', YiseCustomizedBundle);
